import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Zap, ArrowRight, ChevronDown, Star, Phone, Shield, Clock, Award, CheckCircle, MapPin, PhoneCall, BookOpen, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import api from '../lib/api';
import axios from 'axios';

const HERO_BG = "https://images.unsplash.com/photo-1758545760408-754dddae1d09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxwb3dlciUyMGxpbmVzJTIwZWxlY3RyaWNpdHklMjB0b3dlciUyMG5pZ2h0fGVufDB8fHx8MTc3MTU3OTQ4Mnww&ixlib=rb-4.1.0&q=85";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/co7mv4rs_IslandCoastalElectric-MountainOnly-Logo.V3-VECTOR.png";

// Generac generator images for rotation
const GENERAC_IMAGES = [
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/qjmkw6dm_GeneracGenerator-HouseShot.jpg',
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/n1tc9fzh_GeneracGenerator-HouseShot-2.jpg',
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/485id8h9_Generac-Generators-ProductShot.jpg',
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/09aros2b_Generac-Generators-ProductShot-Large.jpg'
];

const getRandomGeneracImage = () => GENERAC_IMAGES[Math.floor(Math.random() * GENERAC_IMAGES.length)];

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [generatorImage] = useState(getRandomGeneracImage());

  useEffect(() => {
    const loadData = async () => {
      try {
        // Seed data first
        await api.seedData();
        
        const [servicesRes, testimonialsRes, faqsRes] = await Promise.all([
          api.getServices(),
          api.getTestimonials(),
          api.getFaqs()
        ]);
        setServices(servicesRes.data);
        setTestimonials(testimonialsRes.data);
        setFaqs(faqsRes.data);

        // Load published blog posts
        try {
          const blogRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`);
          setBlogPosts(blogRes.data.slice(0, 3));
        } catch (e) { console.warn('Blog posts load skipped:', e.message); }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  return (
    <div className="min-h-screen bg-primary-dark" data-testid="home-page">
      {/* Hero Section */}
      <HeroSection />
      
      {/* Features Bar */}
      <FeaturesBar />
      
      {/* Why Choose Us */}
      <WhyChooseUsSection />
      
      {/* Certifications Bar */}
      <section className="py-12 bg-primary-dark border-y border-white/5" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <Link to="/services/bc-hydro-power-smart" className="flex items-center gap-4 group">
              <div className="w-16 h-16 rounded bg-white/10 border border-electric-teal/30 flex items-center justify-center p-1 group-hover:scale-105 transition-transform">
                <span className="text-electric-teal font-bold text-[9px] text-center leading-tight">BC Hydro<br/>Alliance of<br/>Energy<br/>Professionals</span>
              </div>
              <div>
                <p className="text-white/80 font-semibold text-sm group-hover:text-electric-teal transition-colors">BC Hydro Alliance Member</p>
                <p className="text-white/40 text-xs">Commercial/Industrial Rated</p>
              </div>
            </Link>
            <div className="hidden md:block w-px h-10 bg-white/10" />
            <Link to="/services/ev-charger-installation" className="flex items-center gap-4 group">
              <img 
                src="https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/vcyr36jn_tesla-certified-installer.jpg"
                alt="Tesla Certified Installer"
                className="w-16 h-16 object-contain rounded bg-white p-1 group-hover:scale-105 transition-transform"
                data-testid="tesla-home-badge"
              />
              <div>
                <p className="text-white/80 font-semibold text-sm group-hover:text-electric-teal transition-colors">Tesla Certified Installer</p>
                <p className="text-white/40 text-xs">Wall Connector</p>
              </div>
            </Link>
            <div className="hidden md:block w-px h-10 bg-white/10" />
            <Link to="/services/generator-installation" className="flex items-center gap-4 group">
              <img 
                src="https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/40wcnfw6_Generac-Installer.png"
                alt="Generac Certified Installer"
                className="w-16 h-16 object-contain rounded bg-white p-1 group-hover:scale-105 transition-transform"
                data-testid="generac-home-badge"
              />
              <div>
                <p className="text-white/80 font-semibold text-sm group-hover:text-electric-teal transition-colors">Licensed Generac Installer</p>
                <p className="text-white/40 text-xs">Certified Partner</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <ServicesSection services={services} generatorImage={generatorImage} />
      
      {/* Testimonials Section */}
      <TestimonialsSection testimonials={testimonials} />
      
      {/* FAQ Section */}
      <FAQSection faqs={faqs} />

      {/* Featured Blog Posts */}
      {blogPosts.length > 0 && (
        <section className="py-16 md:py-20 bg-primary-dark border-t border-white/5" data-testid="featured-blog">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="text-electric-teal font-bold uppercase tracking-widest text-sm flex items-center gap-2">
                  <BookOpen className="w-4 h-4" /> From Our Blog
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">Latest Electrical Tips</h2>
              </div>
              <Link to="/blog" className="text-electric-teal text-sm font-semibold hover:text-white transition-colors hidden sm:flex items-center gap-1">
                View All Posts <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {blogPosts.map((post, i) => (
                <motion.div key={post.id || i} initial={{opacity:0,y:15}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}>
                  <Link to={`/blog/${post.slug}`} className="block group" data-testid={`blog-card-${i}`}>
                    <div className="bg-secondary-dark rounded-xl border border-white/5 overflow-hidden hover:border-electric-teal/20 transition-colors h-full">
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-electric-teal text-xs font-semibold uppercase tracking-wider">{post.category}</span>
                          <span className="text-white/20 text-xs flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.published_at || post.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <h3 className="text-white font-bold text-lg group-hover:text-electric-teal transition-colors leading-snug mb-3">
                          {post.title}
                        </h3>
                        <p className="text-white/40 text-sm line-clamp-3">{post.excerpt}</p>
                        <span className="inline-flex items-center gap-1 text-electric-teal text-xs font-semibold mt-4 group-hover:gap-2 transition-all">
                          Read Article <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8 sm:hidden">
              <Link to="/blog" className="text-electric-teal text-sm font-semibold">View All Posts &rarr;</Link>
            </div>
          </div>
        </section>
      )}
      
      {/* CTA Section */}
      <CTASection />
      
      {/* Floating Contact Button */}
      <FloatingContact />
    </div>
  );
};

const HeroSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={HERO_BG} 
          alt="Electrical work" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-4 mb-8">
            <img src={LOGO_ICON} alt="" className="h-20 md:h-24 w-auto animate-lightning" />
            <div>
              <span className="text-white font-black text-3xl md:text-4xl tracking-tight block">ISLAND COASTAL</span>
              <span className="text-electric-teal font-bold text-xl md:text-2xl tracking-widest">ELECTRIC</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none mb-6">
            POWERING<br />
            <span className="text-electric-teal text-glow">YOUR WORLD</span>
          </h1>
          
          <p className="text-xl text-white/70 leading-relaxed mb-8 max-w-xl">
            Professional electrical services for commercial, residential, and industrial clients across Vancouver Island.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/book-estimate" data-testid="hero-book-btn">
              <Button className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-white transition-all duration-300 btn-glow">
                <Zap className="w-5 h-5 mr-2" />
                Get Free Estimate
              </Button>
            </Link>
            <Link to="/services" data-testid="hero-services-btn">
              <Button variant="outline" className="border-white/30 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-white/10 hover:border-electric-teal transition-all">
                View Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ChevronDown className="w-8 h-8 text-white/50" />
      </motion.div>
    </section>
  );
};

const FeaturesBar = () => {
  const features = [
    { icon: Shield, text: 'Licensed & Insured' },
    { icon: Clock, text: '24/7 Emergency Service' },
    { icon: Award, text: 'BC Hydro Alliance Member' },
    { icon: Star, text: '5-Star Rated' }
  ];

  return (
    <section className="bg-electric-teal py-6" data-testid="features-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center justify-center gap-3">
              <feature.icon className="w-6 h-6 text-primary-dark" />
              <span className="text-primary-dark font-bold text-sm uppercase tracking-wider">
                {feature.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesSection = ({ services, generatorImage }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const serviceImages = {
    'residential-electrical': 'https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg',
    'commercial-electrical': 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg',
    'ev-charger-installation': 'https://images.unsplash.com/photo-1765272088009-100c96a4cd4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxlbGVjdHJpYyUyMHZlaGljbGUlMjBjaGFyZ2luZyUyMHN0YXRpb24lMjBob21lfGVufDB8fHx8MTc3MTU3NzI3NXww&ixlib=rb-4.1.0&q=85',
    'panel-replacement': 'https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg',
    'generator-installation': generatorImage,
    'lighting-installation': 'https://images.pexels.com/photos/31737859/pexels-photo-31737859.jpeg'
  };

  // Sort services: Residential, Commercial first, then others
  const serviceOrder = ['ev-charger-installation', 'panel-replacement', 'generator-installation', 'bc-hydro-power-smart', 'lighting-installation', 'electrical-planning-reports', 'residential-electrical', 'commercial-electrical'];
  const sortedServices = [...services].sort((a, b) => {
    const aIndex = serviceOrder.indexOf(a.slug);
    const bIndex = serviceOrder.indexOf(b.slug);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  return (
    <section className="py-24 bg-primary-dark" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
            What We Do
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4">
            Our Services
          </h2>
        </motion.div>

        <div className="bento-grid">
          {sortedServices.slice(0, 8).map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={index < 2 ? 'bento-large' : 'bento-medium'}
            >
              <Link 
                to={`/services/${service.slug}`}
                className="block relative overflow-hidden rounded group h-full min-h-[280px]"
                data-testid={`service-card-${service.slug}`}
              >
                <img 
                  src={serviceImages[service.slug] || service.image_url}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 service-card-overlay" />
                <div className="absolute inset-0 border border-white/0 group-hover:border-electric-teal/50 transition-all duration-500 rounded" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white font-bold text-xl mb-2 group-hover:text-electric-teal transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-white/60 text-sm line-clamp-2">
                    {service.short_description}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-electric-teal opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-sm font-bold uppercase tracking-wider">Learn More</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/services" data-testid="view-all-services-btn">
            <Button variant="outline" className="border-white/30 text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-white/10 hover:border-electric-teal transition-all">
              View All Services
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = ({ testimonials }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="py-24 bg-secondary-dark" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
            Testimonials
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4">
            What Our Clients Say
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="testimonial-card p-6 rounded-lg"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-electric-teal text-electric-teal" />
                ))}
              </div>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                &ldquo;{testimonial.content}&rdquo;
              </p>
              <div className="border-t border-white/10 pt-4">
                <p className="text-white font-semibold">{testimonial.customer_name}</p>
                <p className="text-white/50 text-sm">{testimonial.customer_location}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = ({ faqs }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="py-24 bg-primary-dark" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
            FAQ
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4">
            Common Questions
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={faq.id} 
                value={faq.id}
                className="bg-secondary-dark border border-white/10 rounded-lg overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="px-6 py-4 text-left text-white font-semibold hover:text-electric-teal transition-colors faq-trigger">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-white/70">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="py-24 bg-electric-teal" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary-dark tracking-tight mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-primary-dark/70 text-lg mb-8 max-w-2xl mx-auto">
            Schedule your free estimate today and let our master electricians handle all your electrical needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-estimate" data-testid="cta-book-btn">
              <Button className="bg-primary-dark text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-secondary-dark transition-all">
                <Zap className="w-5 h-5 mr-2" />
                Book Free Estimate
              </Button>
            </Link>
            <a href="tel:7789602377" data-testid="cta-call-btn">
              <Button variant="outline" className="border-primary-dark text-primary-dark font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-primary-dark/10 transition-all">
                <Phone className="w-5 h-5 mr-2" />
                778.960.2377
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const WhyChooseUsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const reasons = [
    { icon: Shield, title: 'Licensed & Insured', desc: 'BC Hydro Alliance certified electricians. Full WorkSafeBC coverage on every job.' },
    { icon: Award, title: 'Licensed Generac Installer', desc: 'Factory-trained and authorized for whole-home generator installations.' },
    { icon: Clock, title: 'On-Time, Every Time', desc: 'We respect your schedule. Confirmed appointments with arrival windows.' },
    { icon: CheckCircle, title: 'Transparent Pricing', desc: 'Detailed written estimates before work begins. No surprise charges.' },
    { icon: MapPin, title: 'Locally Owned', desc: 'Proudly serving Vancouver Island communities from Victoria to Duncan.' },
    { icon: Star, title: '5-Star Service', desc: 'Consistently top-rated by homeowners and businesses across the island.' }
  ];

  return (
    <section className="py-24 bg-secondary-dark" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
            Why Choose Us
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4">
            The Island Coastal Difference
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex gap-4 p-6 rounded-lg bg-white/[0.03] border border-white/5 hover:border-electric-teal/30 transition-all"
              data-testid={`why-choose-${i}`}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-electric-teal/10 flex items-center justify-center">
                <r.icon className="w-6 h-6 text-electric-teal" />
              </div>
              <div>
                <h3 className="text-white font-bold mb-1">{r.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{r.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FloatingContact = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="floating-contact">
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="mb-3 bg-secondary-dark border border-white/10 rounded-xl p-5 shadow-2xl w-64"
        >
          <p className="text-white font-bold text-sm mb-3">Get in touch</p>
          <a href="tel:7789602377" className="flex items-center gap-3 text-white/80 hover:text-electric-teal py-2 text-sm transition-colors">
            <PhoneCall className="w-4 h-4" /> 778.960.2377
          </a>
          <Link to="/book-estimate" className="flex items-center gap-3 text-white/80 hover:text-electric-teal py-2 text-sm transition-colors">
            <Zap className="w-4 h-4" /> Book Free Estimate
          </Link>
          <Link to="/contact" className="flex items-center gap-3 text-white/80 hover:text-electric-teal py-2 text-sm transition-colors">
            <ArrowRight className="w-4 h-4" /> Send Message
          </Link>
        </motion.div>
      )}
      <button
        onClick={() => setOpen(!open)}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
          open ? 'bg-white/20 rotate-45' : 'bg-electric-teal hover:scale-110'
        }`}
        data-testid="floating-contact-btn"
      >
        {open ? (
          <span className="text-white text-2xl font-light">+</span>
        ) : (
          <PhoneCall className="w-6 h-6 text-primary-dark" />
        )}
      </button>
    </div>
  );
};

export default HomePage;
